import groupBy from "lodash-es/groupBy";
import { LeadAnnotation } from "../misc/api/DeviceLinkApi/CalEcg";
import { meanBy } from "../misc/math-functions";

/**
   * Returns the Heart Rate
   *
   * @param rr - The RR value in milliseconds
   * @returns The heart rate in beats per minute
*/
export const CalculateHeartRate = (rr: number): number => (60 / rr) * 1000;

/**
   * Returns the QT interval corrected by Bazetts formula
   *
   * @param qt - The QT interval in milliseconds
   * @param rr - The RR value in milliseconds
   * @returns The QTcB value
*/
export const CalculateQTcBazett = (qt: number, rr: number): number => qt / Math.sqrt(rr / 1000);

/**
   * Returns the QT interval corrected by Fridericia formula
   *
   * @param qt - The QT interval in milliseconds
   * @param rr - The RR value in milliseconds
   * @returns The QTcF value
*/
export const CalculateQTcFridericia = (qt: number, rr: number): number => qt / Math.pow(rr / 1000, 1/3);

/**
   * Returns the QT interval corrected by Framingham formula
   *
   * @param qt - The QT interval in milliseconds
   * @param rr - The RR value in milliseconds
   * @returns The QTcFra value
*/
export const CalculateQTcFramingham = (qt: number, rr: number): number => ((qt / 1000) + (0.154 * (1 - (rr / 1000)))) * 1000;

/**
   * Returns the QT interval corrected by Hodges formula
   *
   * @param qt - The QT interval in milliseconds
   * @param heartRate - The Heart Rate in beats per minute
   * @returns The QTcHod value
*/
export const CalculateQTcHodges = (qt: number, heartRate: number): number => qt + (1.75 * (heartRate - 60));

interface EcgParameters {
    rr: number;
    qt: number;
    qrs: number;
    pr: number;
    groupedAnnotationsRhythm: any;
    groupedAnnotationsRepBeat: any;
}

export const SortAnnotations = (an1: LeadAnnotation, an2: LeadAnnotation) => {
    return an1.leadIndex > an2.leadIndex ? 1 :
        (an1.leadIndex < an2.leadIndex ? -1 : an1.annotation < an2.annotation ? -1 : an1.annotation > an2.annotation ? 1 : 0)
}

export const DeriveParametersFromAnnotations = (annotations: LeadAnnotation[]): EcgParameters => {
    if (annotations === undefined)
        return {
            rr: null,
            qt: null,
            qrs: null,
            pr: null,
            groupedAnnotationsRhythm: null,
            groupedAnnotationsRepBeat: null
        };

    const sortedAnnotations = annotations.sort(SortAnnotations);

    const groupedAnnotationsRepBeat = groupBy(sortedAnnotations.filter(a => a.annotation.startsWith("R_")), a => a.annotation + a.leadIndex);
    const groupedAnnotationsRhythm = groupBy(sortedAnnotations.filter(a => !a.annotation.startsWith("R_")), a => a.annotation + a.leadIndex);

    let rr = 0, qt = 0, qrs = 0, pr = 0;
    if (groupedAnnotationsRepBeat.RR0 === undefined && groupedAnnotationsRhythm.RR2 !== undefined) {
        // Use global then lead 2 then lead V4
        rr = groupedAnnotationsRepBeat.R_RR0[0].value ?? meanBy(groupedAnnotationsRhythm.RR2.filter(x => x.value !== null), v => v.value) ?? meanBy(groupedAnnotationsRhythm.RR10.filter(x => x.value !== null), v => v.value);
        qt = groupedAnnotationsRepBeat.R_QT0[0]?.value ?? meanBy(groupedAnnotationsRhythm.QT2.filter(x => x.value !== null), v => v.value) ?? meanBy(groupedAnnotationsRhythm.QT10.filter(x => x.value !== null), v => v.value);
        qrs = groupedAnnotationsRepBeat.R_QRS0[0]?.value ?? meanBy(groupedAnnotationsRhythm.QRS2.filter(x => x.value !== null), v => v.value) ?? meanBy(groupedAnnotationsRhythm.QRS10.filter(x => x.value !== null), v => v.value);
        pr = groupedAnnotationsRepBeat.R_PR0[0]?.value ?? meanBy(groupedAnnotationsRhythm.PR2.filter(x => x.value !== null), v => v.value) ?? meanBy(groupedAnnotationsRhythm.PR10.filter(x => x.value !== null), v => v.value);
    }

    return {
        rr,
        qt,
        qrs,
        pr,
        groupedAnnotationsRhythm,
        groupedAnnotationsRepBeat,
    }
}
